import React, { createContext, useContext, useState, useCallback } from 'react';
import { Product, TransformationStats } from '../services/product';

interface BulkSelection {
  selectedIds: Set<string>;
  category: string | null;
  totalCount: number;
  // Transformation tracking
  stats: TransformationStats | null;
  remaining: number;
  limitApplied: boolean;
  message: string | null;
}

interface BulkProductsCache {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  searchQuery: string;
  lastFetched: number;
  selectionSnapshot: string; // Hash of selected IDs to detect changes
}

interface BulkSelectionContextType {
  selection: BulkSelection;
  isSelectionActive: boolean;
  cache: BulkProductsCache | null;
  selectByCategory: (
    category: string,
    productIds: string[],
    totalCount: number,
    stats?: TransformationStats,
    remaining?: number,
    limitApplied?: boolean,
    message?: string
  ) => void;
  addToSelection: (productId: string) => void;
  removeFromSelection: (productId: string) => void;
  clearSelection: () => void;
  selectAll: (productIds: string[]) => void;
  unselectAll: () => void;
  isSelected: (productId: string) => boolean;
  // Cache methods
  setCachedProducts: (products: Product[], pagination: any, searchQuery: string) => void;
  getCachedProducts: (searchQuery: string) => BulkProductsCache | null;
  invalidateCache: () => void;
  isCacheValid: (searchQuery: string) => boolean;
}

const BulkSelectionContext = createContext<BulkSelectionContextType | undefined>(undefined);

interface BulkSelectionProviderProps {
  children: React.ReactNode;
}

export function BulkSelectionProvider({ children }: BulkSelectionProviderProps) {
  const [selection, setSelection] = useState<BulkSelection>({
    selectedIds: new Set(),
    category: null,
    totalCount: 0,
    stats: null,
    remaining: 0,
    limitApplied: false,
    message: null,
  });

  const [cache, setCache] = useState<BulkProductsCache | null>(null);

  const isSelectionActive = selection.selectedIds.size > 0;

  // Helper function to create selection snapshot for cache validation
  const createSelectionSnapshot = useCallback((selectedIds: Set<string>) => {
    return Array.from(selectedIds).sort().join(',');
  }, []);

  const selectByCategory = useCallback((
    category: string,
    productIds: string[],
    totalCount: number,
    stats?: TransformationStats,
    remaining?: number,
    limitApplied?: boolean,
    message?: string
  ) => {
    setSelection({
      selectedIds: new Set(productIds),
      category,
      totalCount,
      stats: stats || null,
      remaining: remaining || 0,
      limitApplied: limitApplied || false,
      message: message || null,
    });
    // Clear cache when selection changes
    setCache(null);
  }, []);

  const addToSelection = useCallback((productId: string) => {
    setSelection(prev => {
      const newSelectedIds = new Set(prev.selectedIds);
      newSelectedIds.add(productId);
      return {
        ...prev,
        selectedIds: newSelectedIds,
        totalCount: newSelectedIds.size,
      };
    });
    // Clear cache when selection changes
    setCache(null);
  }, []);

  const removeFromSelection = useCallback((productId: string) => {
    setSelection(prev => {
      const newSelectedIds = new Set(prev.selectedIds);
      newSelectedIds.delete(productId);
      return {
        ...prev,
        selectedIds: newSelectedIds,
        totalCount: newSelectedIds.size,
      };
    });
    // Update cache to remove the product if it exists
    setCache(prevCache => {
      if (!prevCache) return null;
      return {
        ...prevCache,
        products: prevCache.products.filter(p => p.id !== productId),
        pagination: {
          ...prevCache.pagination,
          total: Math.max(0, prevCache.pagination.total - 1)
        }
      };
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({
      selectedIds: new Set(),
      category: null,
      totalCount: 0,
      stats: null,
      remaining: 0,
      limitApplied: false,
      message: null,
    });
    // Clear cache when selection is cleared
    setCache(null);
  }, []);

  const selectAll = useCallback((productIds: string[]) => {
    setSelection(prev => ({
      ...prev,
      selectedIds: new Set(productIds),
      totalCount: productIds.length,
    }));
    // Clear cache when selection changes
    setCache(null);
  }, []);

  const unselectAll = useCallback(() => {
    setSelection(prev => ({
      ...prev,
      selectedIds: new Set(),
      totalCount: 0,
    }));
    // Clear cache when selection changes
    setCache(null);
  }, []);

  const isSelected = useCallback((productId: string) => {
    return selection.selectedIds.has(productId);
  }, [selection.selectedIds]);

  // Cache methods
  const setCachedProducts = useCallback((products: Product[], pagination: any, searchQuery: string) => {
    const selectionSnapshot = createSelectionSnapshot(selection.selectedIds);
    setCache({
      products,
      pagination,
      searchQuery,
      lastFetched: Date.now(),
      selectionSnapshot,
    });
  }, [selection.selectedIds, createSelectionSnapshot]);

  const getCachedProducts = useCallback((searchQuery: string): BulkProductsCache | null => {
    if (!cache) return null;
    
    const currentSnapshot = createSelectionSnapshot(selection.selectedIds);
    const isSameSelection = cache.selectionSnapshot === currentSnapshot;
    const isSameSearch = cache.searchQuery === searchQuery;
    const isNotExpired = Date.now() - cache.lastFetched < 5 * 60 * 1000; // 5 minutes

    if (isSameSelection && isSameSearch && isNotExpired) {
      return cache;
    }
    
    return null;
  }, [cache, selection.selectedIds, createSelectionSnapshot]);

  const invalidateCache = useCallback(() => {
    setCache(null);
  }, []);

  const isCacheValid = useCallback((searchQuery: string): boolean => {
    return getCachedProducts(searchQuery) !== null;
  }, [getCachedProducts]);

  const value = {
    selection,
    isSelectionActive,
    cache,
    selectByCategory,
    addToSelection,
    removeFromSelection,
    clearSelection,
    selectAll,
    unselectAll,
    isSelected,
    setCachedProducts,
    getCachedProducts,
    invalidateCache,
    isCacheValid,
  };

  return (
    <BulkSelectionContext.Provider value={value}>
      {children}
    </BulkSelectionContext.Provider>
  );
}

export function useBulkSelection() {
  const context = useContext(BulkSelectionContext);
  if (context === undefined) {
    throw new Error('useBulkSelection must be used within a BulkSelectionProvider');
  }
  return context;
}